import type { Habit, HabitLog } from '@obv/api-client';
import {
  EVERYONE, FAMILY, SCHEDULE_LABEL, habitAppliesOn, heatmap, matchesFilter, streak, streakLabel, weekDays, weekProgress,
  weekStartIndex, weekdayHeaders,
} from '@obv/core';
import { personColor } from '@obv/ui';
import { useRef } from 'react';
import { useSetHabitLog } from '@/queries/chores';
import { useHabitData } from '../choreData';
import { memberLabel, useDisplay } from '../context';
import { useDisplayUi } from '../store';
import { Button, Card, CheckCircle, Chip, ScreenHeader, Unavailable } from '../ui';

const LONG_PRESS_MS = 550;

export function HabitsScreen() {
  const ctx = useDisplay();
  const { filter, setFilter, openSheet } = useDisplayUi();
  const { habits, logs } = useHabitData();
  const portrait = ctx.orientation === 'portrait';
  const list = habits.data ? habits.data.filter((h) => !h.archived && matchesFilter(h, filter)) : [];
  const chips = [{ id: EVERYONE, label: 'Everyone' }, ...ctx.boot.members.map((m) => ({ id: m.id, label: m.display_name })), { id: FAMILY, label: 'Family' }];

  return (
    <>
      <ScreenHeader
        eyebrow="Habits & streaks"
        title="Little things, every day."
        right={
          <Button variant="primary" onClick={() => openSheet({ mode: 'addHabit' })}>
            + Add a habit
          </Button>
        }
      />
      <div className="mt-4 flex flex-none flex-wrap">
        {chips.map((c) => (
          <div key={c.id} className="mb-2 mr-2">
            <Chip selected={filter === c.id} onClick={() => setFilter(c.id)}>
              {c.label}
            </Chip>
          </div>
        ))}
      </div>
      <div className="scroll-y mt-2 min-h-0 flex-1">
        {habits.isLoading ? (
          <Unavailable>Loading habits…</Unavailable>
        ) : habits.isError && !habits.data ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="text-[15px]">Couldn’t load habits.</div>
            <Button className="mt-3" onClick={() => habits.refetch()}>
              Try again
            </Button>
          </div>
        ) : list.length === 0 ? (
          <div className="flex h-full min-h-[300px] flex-col items-center justify-center text-center">
            <div className="font-serif text-[24px]">No habits yet.</div>
            <div className="mt-2 text-[14px] text-muted">Start small — a walk, ten minutes of reading, dinner without phones.</div>
          </div>
        ) : (
          <div className="grid pb-2" style={{ gridTemplateColumns: portrait ? 'minmax(0,1fr)' : 'minmax(0,1fr) minmax(0,1fr)', gridGap: 14 }}>
            {list.map((h, i) => (
              <HabitCard key={h.id} habit={h} logs={logs.data || []} tone={(i % 6) + 1} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function HabitCard({ habit, logs, tone }: { habit: Habit; logs: HabitLog[]; tone: number }) {
  const ctx = useDisplay();
  const openSheet = useDisplayUi((s) => s.openSheet);
  const setLog = useSetHabitLog();
  const today = ctx.clock.dateKey;
  const ws = ctx.prefs.week_starts;
  const wsi = weekStartIndex(ws);
  const who = memberLabel(ctx, habit.member_id);
  const color = habit.color_key ? personColor(habit.color_key) : personColor(who.colorKey);
  const done: Record<string, boolean> = {};
  for (const l of logs) if (l.habit_id === habit.id && l.done) done[l.date] = true;
  const n = streak(habit, logs, today, wsi);
  const prog = weekProgress(habit, logs, today, wsi);
  const days = weekDays(today, ws);
  const heads = weekdayHeaders(ws);
  const appliesToday = habitAppliesOn(habit.schedule, today);
  const toggle = (date: string) => setLog.mutate({ habitId: habit.id, date, done: !done[date] });

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startHold = () => {
    timer.current = setTimeout(() => openSheet({ mode: 'editHabit', habitId: habit.id }), LONG_PRESS_MS);
  };
  const cancelHold = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  return (
    <Card tone={tone} className="flex flex-col px-5 py-4">
      <div className="flex items-start">
        <button
          type="button"
          className="tap min-w-0 flex-1 text-left"
          onTouchStart={startHold}
          onTouchEnd={cancelHold}
          onTouchMove={cancelHold}
          onMouseDown={startHold}
          onMouseUp={cancelHold}
          onMouseLeave={cancelHold}
          onContextMenu={(e) => e.preventDefault()}
          aria-label={`${habit.name}. Hold to edit.`}
        >
          <span className="flex items-center">
            <span className="mr-2 h-[10px] w-[10px] flex-none rounded-full" style={{ background: color }} />
            <span className="ellipsis text-[17px]">{habit.name}</span>
          </span>
          <span className="mt-[3px] block text-[12px] text-muted">
            {who.name} · {SCHEDULE_LABEL[habit.schedule]}
            {habit.schedule === 'weekly' ? ` · ${habit.target_per_week}× a week` : ''}
          </span>
        </button>
        <div className="-mr-2 -mt-2 flex flex-col items-center">
          <CheckCircle
            checked={!!done[today]}
            onClick={() => toggle(today)}
            label={`${habit.name} today${done[today] ? ', done' : ''}`}
            color={color}
            disabled={!appliesToday}
          />
        </div>
      </div>

      <div className="mt-3 flex items-end justify-between">
        <div>
          <div className="font-serif text-[26px] leading-none">{streakLabel(habit, n)}</div>
          <div className="mt-1 text-[12px] text-muted">
            streak · {prog.done} of {prog.target} this week
          </div>
        </div>
        <HeatGrid habit={habit} logs={logs} today={today} wsi={wsi} color={color} />
      </div>

      <div className="mt-3 flex justify-between border-t border-line pt-2">
        {days.map((d, i) => {
          const future = d > today;
          const off = !habitAppliesOn(habit.schedule, d);
          const on = !!done[d];
          return (
            <button
              key={d}
              type="button"
              disabled={future}
              aria-pressed={on}
              aria-label={`${heads[i]} ${Number(d.slice(8))}${on ? ', done' : ''}`}
              onClick={() => toggle(d)}
              className="tap flex h-[44px] w-[40px] flex-col items-center justify-center disabled:opacity-30"
              style={{ opacity: off && !on ? 0.4 : undefined }}
            >
              <span className="text-[11px] text-muted" style={{ fontWeight: d === today ? 700 : 400 }}>
                {heads[i]!.charAt(0)}
              </span>
              <span
                className="mt-[3px] h-[16px] w-[16px] rounded-full"
                style={{ border: `1.5px solid ${on ? color : 'var(--bd)'}`, background: on ? color : 'transparent' }}
              />
            </button>
          );
        })}
      </div>
    </Card>
  );
}

function HeatGrid({ habit, logs, today, wsi, color }: { habit: Habit; logs: HabitLog[]; today: string; wsi: 0 | 1; color: string }) {
  const rows = heatmap(habit, logs, today, wsi);
  return (
    <div aria-hidden className="flex">
      {rows.map((row, w) => (
        <div key={w} className="flex flex-col" style={{ marginLeft: w ? 3 : 0 }}>
          {row.map((c, d) => (
            <span
              key={c.date}
              className="h-[8px] w-[8px] rounded-[2px]"
              style={{
                marginTop: d ? 3 : 0,
                background: c.state === 'done' ? color : c.state === 'missed' ? 'var(--track)' : 'transparent',
                boxShadow: c.state === 'future' ? 'none' : 'inset 0 0 0 1px var(--line)',
                opacity: c.state === 'future' ? 0.25 : 1,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
